import { Plate } from '../components/Card';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import CashSplit from '../components/CashSplit';
import PayrollEditor from './PayrollEditor';

const fmt = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Math.round(value).toLocaleString('ru-RU');
};
const fmtPct = (value) => (value === null || value === undefined ? '—' : `${Math.round(value)}%`);

function localMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}
function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const controlStyle = {
  height: 40,
  boxSizing: 'border-box',
  background: 'var(--bg)',
  color: 'var(--text1)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '0 12px',
  fontSize: 13,
  fontWeight: 600,
  width: '100%',
};

const cellControl = { ...controlStyle, height: 32, width: 160 };

function sourceHint(row) {
  const parts = [];
  if (row.payroll) parts.push(`из ведомости ${fmt(row.payroll)}`);
  if (row.bank) parts.push(`из банка ${fmt(row.bank)}`);
  const manual = (row.entry || 0) - (row.payroll || 0) - (row.bank || 0);
  if (Math.abs(manual) >= 1) parts.push(`вручную ${fmt(manual)}`);
  return parts.length ? parts.join(' · ') : undefined;
}

const rowButtonStyle = {
  height: 32,
  minWidth: 88,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: '#fff',
  cursor: 'pointer',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

function TabButton({ active, children, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      height: 36, minWidth: 120, padding: '0 16px', whiteSpace: 'nowrap', flexShrink: 0,
      borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
      background: active ? 'var(--accent)' : 'var(--bg3)',
      color: active ? '#fff' : 'var(--text1)',
    }}>{children}</button>
  );
}

function Field({ label, children, style }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, ...style }}>
      <span style={{ fontSize: 12, color: 'var(--text2)', height: 16, whiteSpace: 'nowrap' }}>{label || ' '}</span>
      {children}
    </label>
  );
}

function Kpi({ label, plan, fact, tone }) {
  const pct = plan ? Math.round((fact || 0) / plan * 100) : null;
  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 14, padding: '16px 18px 18px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone, whiteSpace: 'nowrap' }}>{fmt(fact)}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, gap: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>план {fmt(plan)}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: tone }}>{fmtPct(pct)}</span>
      </div>
    </div>
  );
}

function PlanModal({ row, onSave, onClose }) {
  const [val, setVal] = useState(row?.plan ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(30,36,37,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
    }}>
      <div onClick={(event) => event.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 24, width: 360,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>План</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>{row.name}</div>
        <input autoFocus type="number" value={val} onChange={(event) => setVal(event.target.value)} style={controlStyle} />
        {error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" disabled={saving} onClick={async () => {
            setSaving(true);
            setError('');
            try {
              await onSave(parseFloat(val) || 0);
            } catch (err) {
              setError(err?.response?.data?.detail || 'Не удалось сохранить');
              setSaving(false);
            }
          }} style={{
            flex: 1, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer',
            background: 'var(--accent)', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap',
          }}>{saving ? '…' : 'Сохранить'}</button>
          <button type="button" onClick={onClose} style={{
            flex: 1, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer',
            background: 'var(--bg3)', color: 'var(--text1)', fontWeight: 700,
          }}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

const QUICK_ARTICLES = [
  ['Арендные', 'Аренда'],
  ['Маркетинг', 'Маркетинг'],
  ['Сервисы', 'Сервисы'],
  ['Транспортные', 'Транспорт'],
  ['Покупка товаров', 'Товар'],
  ['Прочие расходы', 'Прочее'],
  ['Услуги (', 'Подрядчики'],
  ['Банковские', 'Банк'],
  ['связь', 'Связь'],
  ['Налоги и', 'Налоги'],
  ['Поступления основные', 'Поступления'],
  ['Доход розницы', 'Доход розницы'],
  ['Прочие доходы', 'Прочий доход'],
];

function choiceStyle(active) {
  return {
    width: '100%', height: 40, padding: '0 8px', borderRadius: 8, cursor: 'pointer',
    fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    background: active ? 'var(--accent)' : '#fff',
    color: active ? '#fff' : 'var(--text1)',
  };
}

function PayrollStrip({ data }) {
  if (!data) return null;
  const rows = (data.projects || []).filter((item) => item.people || item.accrued || item.paid_25 || item.paid_10 || item.tax);
  const sum = (key) => rows.reduce((total, item) => total + (item[key] || 0), 0);
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: rows.length ? 10 : 0 }}>
        <div style={{ fontWeight: 800 }}>ФОТ из ведомости</div>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>{data.note}</div>
      </div>
      {!rows.length && <div style={{ fontSize: 13, color: 'var(--text2)' }}>За этот месяц в зарплатном реестре пока нет сумм.</div>}
      {!!rows.length && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          {rows.map((item) => (
            <div key={item.unit} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
              <div style={{ fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>начислено {fmt(item.accrued)}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>выплата 25-го {fmt(item.paid_25)}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>хвост 10-го {fmt(item.paid_10)}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>налоги {fmt(item.tax)}</div>
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, whiteSpace: 'nowrap' }}>к выплате {fmt(Math.max((item.accrued || 0) - (item.paid_25 || 0) - (item.paid_10 || 0), 0))}</div>
              {item.from_sheet && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>в этом месяце уже есть в перенесённой таблице</div>}
            </div>
          ))}
          <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontWeight: 800 }}>Всего</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>начислено {fmt(sum('accrued'))}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>выплата 25-го {fmt(sum('paid_25'))}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>хвост 10-го {fmt(sum('paid_10'))}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>налоги {fmt(sum('tax'))}</div>
            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, whiteSpace: 'nowrap' }}>к выплате {fmt(Math.max(sum('accrued') - sum('paid_25') - sum('paid_10'), 0))}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectBoard({ projects, pace, onPick, onPlan, active }) {
  if (!projects?.length) return null;
  const closed = pace?.closed_calendar;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontWeight: 800 }}>План и тренд по проектам</div>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>
          {closed
            ? 'Месяц прошёл: тренд совпадает с фактом'
            : `Тренд = факт ÷ ${pace?.elapsed || 0} дн. × ${pace?.days || 0} дн.`}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))', gap: 8 }}>
        {projects.map((item) => {
          const ahead = item.pct != null && item.pct >= 100;
          return (
            <div key={item.code} style={{
              background: '#fff', borderRadius: 14, padding: 12, minWidth: 0,
              border: `1px solid ${active === item.code ? 'var(--accent)' : 'var(--border)'}`,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <button type="button" title={item.full} onClick={() => onPick(item.code)} style={{
                width: '100%', height: 32, padding: '0 8px', border: 'none', borderRadius: 8, cursor: 'pointer',
                background: active === item.code ? 'var(--accent)' : 'var(--bg3)',
                color: active === item.code ? '#fff' : 'var(--text1)',
                fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{item.name}</button>
              <button type="button" title={`План ${fmt(item.plan)}`} disabled={!item.editable} onClick={() => item.editable && onPlan(item)} style={{
                width: '100%', height: 32, padding: '0 8px', borderRadius: 8, cursor: item.editable ? 'pointer' : 'default',
                border: '1px solid var(--border)', background: '#fff', color: 'var(--text1)',
                fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{`План ${fmt(item.plan)}`}</button>
              <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>факт {fmt(item.fact)}</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: ahead ? 'var(--green)' : 'var(--text1)', whiteSpace: 'nowrap' }}>
                тренд {fmt(item.trend)}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: ahead ? 'var(--green)' : 'var(--yellow)', whiteSpace: 'nowrap' }}>
                {fmtPct(item.pct)} плана
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function toneOf(group, fact, plan) {
  if (fact === null || fact === undefined || !plan) return 'var(--text1)';
  if (group === 'expense') return fact <= plan ? 'var(--green)' : 'var(--red)';
  return fact >= plan ? 'var(--green)' : 'var(--red)';
}

export default function PlanFact() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (searchParams.get('tab') === 'payroll' ? 'payroll' : 'dds'));
  const [period, setPeriod] = useState(localMonth());
  const [unit, setUnit] = useState('retail');
  const [meta, setMeta] = useState(null);
  const [report, setReport] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planRow, setPlanRow] = useState(null);
  const [planProject, setPlanProject] = useState(null);
  const [sheet, setSheet] = useState('');
  const [sheetQuery, setSheetQuery] = useState('');
  const [archive, setArchive] = useState(null);
  const [archiveOffset, setArchiveOffset] = useState(0);
  const [payroll, setPayroll] = useState(null);
  const [projects, setProjects] = useState(['retail']);
  const [queuePick, setQueuePick] = useState({});
  const [draft, setDraft] = useState({
    entry_date: localToday(),
    article_code: '',
    amount: '',
    statement: 'both',
    note: '',
  });

  useEffect(() => {
    if (searchParams.get('tab') === 'payroll') setTab('payroll');
    else setTab((prev) => (prev === 'payroll' ? 'dds' : prev));
  }, [searchParams]);

  const selectTab = (next) => {
    setTab(next);
    const current = searchParams.get('tab');
    if (next === 'payroll') {
      if (current !== 'payroll') setSearchParams({ tab: 'payroll' }, { replace: true });
    } else if (current) {
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    api.get('/finance/meta').then((response) => {
      setMeta(response.data);
      const articles = response.data.articles || [];
      const rent = articles.find((item) => item.name.startsWith('Арендные'));
      const first = rent || articles.find((item) => item.can_entry);
      if (first) setDraft((prev) => ({ ...prev, article_code: prev.article_code || first.code }));
    }).catch((err) => setError(err?.response?.data?.detail || 'Не удалось открыть учёт'));
  }, []);

  useEffect(() => {
    api.get('/finance/payroll', { params: { period } })
      .then((response) => setPayroll(response.data))
      .catch(() => setPayroll(null));
  }, [period]);

  useEffect(() => {
    if (tab !== 'dds' && tab !== 'pnl') return undefined;
    setLoading(true);
    setError('');
    api.get('/finance/statement', { params: { period, statement: tab, unit } })
      .then((response) => setReport(response.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Не удалось собрать отчёт'))
      .finally(() => setLoading(false));
    return undefined;
  }, [tab, period, unit]);

  const loadEntries = () => {
    api.get('/finance/entries', { params: { period } })
      .then((response) => setEntries(response.data))
      .catch(() => setEntries([]));
  };

  useEffect(() => {
    if (tab === 'entry') loadEntries();
  }, [tab, period]);

  useEffect(() => {
    if (tab !== 'archive' || !sheet) return undefined;
    api.get('/finance/archive', { params: { name: sheet, offset: archiveOffset, limit: 120 } })
      .then((response) => setArchive(response.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Вкладка не открылась'));
    return undefined;
  }, [tab, sheet, archiveOffset]);

  const units = meta?.units || [];
  const entryArticles = useMemo(
    () => (meta?.articles || []).filter((item) => item.can_entry),
    [meta],
  );
  const sheets = useMemo(() => {
    const query = sheetQuery.trim().toLowerCase();
    return (meta?.sheets || []).filter((item) => !query || item.name.toLowerCase().includes(query));
  }, [meta, sheetQuery]);

  const quickArticles = useMemo(() => {
    const list = meta?.articles || [];
    return QUICK_ARTICLES.map(([needle, label]) => {
      const article = list.find((item) => item.can_entry && item.name.toLowerCase().includes(needle.toLowerCase()));
      return article ? { ...article, label } : null;
    }).filter(Boolean);
  }, [meta]);

  const saveEntry = async () => {
    setError('');
    const total = parseFloat(String(draft.amount).replace(/\s/g, '').replace(',', '.')) || 0;
    if (!projects.length) throw new Error('Выберите проект');
    if (!total) throw new Error('Укажите сумму');
    const cents = Math.round(total * 100);
    const base = Math.floor(cents / projects.length);
    const amounts = projects.map((_, index) => (
      (index === projects.length - 1 ? cents - base * (projects.length - 1) : base) / 100
    ));
    await api.post('/finance/entries/batch', {
      entries: projects.map((code, index) => ({
        entry_date: draft.entry_date,
        unit_code: code,
        article_code: draft.article_code,
        amount: amounts[index],
        statement: draft.statement,
        note: draft.note,
      })),
    });
    setDraft((prev) => ({ ...prev, amount: '', note: '' }));
    loadEntries();
  };

  const pickOf = (row) => queuePick[row.bank_txn_id] || {
    unit: row.suggest_unit || 'retail',
    article: row.suggest_article || '',
  };

  const assignBank = async (row) => {
    const pick = pickOf(row);
    if (!pick.unit || !pick.article) {
      setError('Выберите проект и статью');
      return;
    }
    setError('');
    await api.post(`/finance/bank/${row.bank_txn_id}/assign`, {
      unit_code: pick.unit,
      article_code: pick.article,
    });
    loadEntries();
  };

  const summary = report?.summary || {};
  const rows = report?.rows || [];

  return (
    <div>
      <div className="kp-hero">
        <Plate>Финансы · учёт</Plate>
        <h1 style={{ margin: '8px 0 0' }}>ДДС и P&L</h1>
        <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13 }}>
          История из таблицы перенесена. Новые суммы разносятся здесь, по дням.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <TabButton active={tab === 'dds'} onClick={() => selectTab('dds')}>ДДС</TabButton>
        <TabButton active={tab === 'pnl'} onClick={() => selectTab('pnl')}>P&L</TabButton>
        <TabButton active={tab === 'entry'} onClick={() => selectTab('entry')}>Разноска</TabButton>
        <TabButton active={tab === 'archive'} onClick={() => selectTab('archive')}>Архив</TabButton>
        <TabButton active={tab === 'payroll'} onClick={() => selectTab('payroll')}>Зарплата</TabButton>
        {tab !== 'payroll' && (
          <div style={{ marginLeft: 'auto' }}>
            <input type="month" value={period} onChange={(event) => setPeriod(event.target.value)} style={{ ...controlStyle, width: 160 }} />
          </div>
        )}
      </div>

      {error && tab !== 'payroll' && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {tab === 'payroll' && <PayrollEditor />}

      {(tab === 'dds' || tab === 'pnl') && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: 8, marginBottom: 16 }}>
            {units.map((item) => (
              <button key={item.code} type="button" title={item.full} onClick={() => setUnit(item.code)} style={{
                width: '100%', height: 36, minWidth: 0, padding: '0 8px',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                border: `1px solid ${unit === item.code ? 'var(--accent)' : 'var(--border)'}`,
                background: unit === item.code ? 'var(--accent)' : '#fff',
                color: unit === item.code ? '#fff' : 'var(--text1)',
              }}>{item.name}</button>
            ))}
          </div>

          <ProjectBoard
            projects={report?.projects}
            pace={report?.pace}
            active={unit}
            onPick={setUnit}
            onPlan={setPlanProject}
          />
          {tab === 'pnl' && report?.result && (
            <div style={{
              background: report.result.preliminary ? 'rgba(224,138,0,0.12)' : 'rgba(14,159,110,0.12)',
              border: '1px solid var(--border)', borderRadius: 14, padding: '12px 16px', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 800 }}>{report.result.label}</div>
              <div style={{ fontSize: 13, color: 'var(--text1)', marginTop: 4 }}>
                {report.result.preliminary
                  ? `Начисления из ведомости уже в прибыли. К выплате ${fmt(report.result.to_pay)}. Деньги в ДДС встанут 25-го и 10-го, не раньше.`
                  : 'Обе выплаты месяца прошли, касса закрыта. Это результат месяца.'}
              </div>
            </div>
          )}
          <PayrollStrip data={payroll} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Kpi label="Поступления" plan={summary.income?.plan} fact={summary.income?.fact} tone="var(--accent)" />
            <Kpi label="Расходы" plan={summary.expense?.plan} fact={summary.expense?.fact} tone="var(--red)" />
            <Kpi label="Операционная прибыль" plan={summary.profit?.plan} fact={summary.profit?.fact} tone={(summary.profit?.fact || 0) >= 0 ? 'var(--green)' : 'var(--red)'} />
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text2)' }}>
              Факт = перенос из таблицы + разноска этого месяца. План статьи меняется по клику на число.
            </div>
            {loading ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>Собираем отчёт…</div>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 360px)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Статья', 'План', 'Факт', ...(tab === 'dds' ? (report?.weeks || []).map((label, index) => `${index + 1} нед ${label}`) : ['Откл.']), '%'].map((label, index) => (
                        <th key={label} style={{
                          position: 'sticky', top: 0, zIndex: index === 0 ? 3 : 2,
                          left: index === 0 ? 0 : undefined,
                          background: '#fff', textAlign: index === 0 ? 'left' : 'right',
                          padding: '10px 12px', color: 'var(--text2)', fontSize: 11, fontWeight: 700,
                          borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                          minWidth: index === 0 ? 240 : 88,
                        }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const background = row.code === 'op_profit'
                        ? 'rgba(14,159,110,0.08)'
                        : row.code === 'expenses'
                          ? 'rgba(224,72,72,0.06)'
                          : row.code === 'income_main'
                            ? 'rgba(0,167,220,0.08)'
                            : '#fff';
                      const numbers = tab === 'dds'
                        ? [row.plan, row.fact, ...(row.weeks || [])]
                        : [row.plan, row.fact, row.fact === null || row.plan === null ? null : row.fact - row.plan];
                      return (
                        <tr key={row.code} style={{ background }}>
                          <td style={{
                            position: 'sticky', left: 0, background, zIndex: 1,
                            padding: '8px 12px', paddingLeft: 12 + row.level * 16,
                            fontWeight: row.bold ? 800 : 500, borderBottom: '1px solid var(--border)',
                            minWidth: 240,
                          }}>{row.name}</td>
                          {numbers.map((value, index) => (
                            <td key={index} title={index === 1 ? sourceHint(row) : undefined} onClick={() => {
                              if (index === 0 && row.editable_plan) setPlanRow(row);
                            }} style={{
                              padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                              borderBottom: '1px solid var(--border)',
                              fontWeight: index === 1 || row.bold ? 700 : 500,
                              color: index === 1 ? toneOf(row.group, row.fact, row.plan) : 'var(--text1)',
                              cursor: index === 0 && row.editable_plan ? 'pointer' : 'default',
                            }}>{fmt(value)}</td>
                          ))}
                          <td style={{
                            padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                            borderBottom: '1px solid var(--border)', fontWeight: 700,
                            color: toneOf(row.group, row.fact, row.plan),
                          }}>{fmtPct(row.pct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'entry' && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 16 }}>
          <div style={{ background: 'rgba(224,138,0,0.12)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 13 }}>
            ФОТ в прибыль попадает из начислений в разделе «Зарплата», как только Натали сохранит ведомость — за 1–2 дня до 25-го и до 10-го. Сумма к выплате видна сразу. В ДДС эти деньги встанут только в дату выплаты.
          </div>
          <PayrollStrip data={payroll} />
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Проект. Можно отметить несколько — сумма разделится поровну.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
            {units.filter((item) => item.code !== 'company').map((item) => (
              <button key={item.code} type="button" title={item.full} onClick={() => setProjects((prev) => (
                prev.includes(item.code) ? prev.filter((code) => code !== item.code) : [...prev, item.code]
              ))} style={choiceStyle(projects.includes(item.code))}>{item.name}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Статья</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
            {quickArticles.map((item) => (
              <button key={item.code} type="button" title={item.name} onClick={() => setDraft((prev) => ({ ...prev, article_code: item.code }))} style={choiceStyle(draft.article_code === item.code)}>{item.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Дата" style={{ width: 160 }}>
              <input type="date" value={draft.entry_date} onChange={(event) => {
                const entry_date = event.target.value;
                setDraft((prev) => ({ ...prev, entry_date }));
                if (entry_date) setPeriod(entry_date.slice(0, 7));
              }} style={controlStyle} />
            </Field>
            <Field label="Другая статья" style={{ flex: '1 1 220px' }}>
              <select value={draft.article_code} onChange={(event) => setDraft((prev) => ({ ...prev, article_code: event.target.value }))} style={controlStyle}>
                {['income', 'expense', 'below', 'result'].map((group) => {
                  const options = entryArticles.filter((item) => item.group === group);
                  if (!options.length) return null;
                  const title = { income: 'Доходы', expense: 'Расходы', below: 'Ниже прибыли', result: 'Результат' }[group];
                  return (
                    <optgroup key={group} label={title}>
                      {options.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </Field>
            <Field label="Сумма" style={{ width: 150 }}>
              <input type="number" value={draft.amount} placeholder="0" onChange={(event) => setDraft((prev) => ({ ...prev, amount: event.target.value }))} style={controlStyle} />
            </Field>
            <Field label="Куда" style={{ width: 240 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { id: 'dds', label: 'ДДС' },
                  { id: 'pnl', label: 'P&L' },
                  { id: 'both', label: 'Оба' },
                ].map((item) => (
                  <button key={item.id} type="button" onClick={() => setDraft((prev) => ({ ...prev, statement: item.id }))} style={{
                    ...choiceStyle(draft.statement === item.id), flex: '1 1 0', width: 'auto',
                  }}>{item.label}</button>
                ))}
              </div>
            </Field>
            <Field label="Комментарий" style={{ flex: '1 1 180px' }}>
              <input value={draft.note} placeholder="контрагент, за что" onChange={(event) => setDraft((prev) => ({ ...prev, note: event.target.value }))} style={controlStyle} />
            </Field>
            <Field label=" ">
              <button type="button" onClick={() => saveEntry().catch((err) => setError(err?.response?.data?.detail || err.message || 'Не сохранилось'))} style={{
                height: 40, minWidth: 140, padding: '0 16px', border: 'none', borderRadius: 8,
                background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>Добавить</button>
            </Field>
          </div>
          {projects.length > 1 && draft.amount && (
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 8 }}>
              Сумма разделится поровну на {projects.length} проекта. Неравные доли вносите отдельными строками.
            </div>
          )}

          <CashSplit period={period} units={units} entryArticles={entryArticles} quickArticles={quickArticles} onSaved={loadEntries} onPeriod={setPeriod} />

          <div style={{ marginTop: 18, fontSize: 13, color: 'var(--text2)' }}>
            За {period}: {entries.filter((row) => !row.unmatched).length} строк на {fmt(entries.filter((row) => !row.unmatched).reduce((sum, row) => sum + (row.amount || 0), 0))}
            {entries.some((row) => row.unmatched) ? ` · к разноске ${entries.filter((row) => row.unmatched).length}` : ''}
          </div>
          <div style={{ overflow: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Дата', 'Подразделение', 'Статья', 'Сумма', 'Куда', 'Комментарий', ''].map((label) => (
                    <th key={label} style={{
                      textAlign: 'left', padding: '8px 10px', color: 'var(--text2)', fontSize: 11,
                      borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                    }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => {
                  const pick = row.unmatched ? pickOf(row) : null;
                  return (
                  <tr key={row.unmatched ? `bank-${row.bank_txn_id}` : row.id}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{row.entry_date}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                      {row.unmatched ? (
                        <select value={pick.unit} onChange={(event) => setQueuePick((prev) => ({
                          ...prev,
                          [row.bank_txn_id]: { ...pick, unit: event.target.value },
                        }))} style={cellControl}>
                          {units.filter((item) => item.code !== 'company').map((item) => (
                            <option key={item.code} value={item.code}>{item.name}</option>
                          ))}
                        </select>
                      ) : row.unit_name}
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      {row.unmatched ? (
                        <select value={pick.article} onChange={(event) => setQueuePick((prev) => ({
                          ...prev,
                          [row.bank_txn_id]: { ...pick, article: event.target.value },
                        }))} style={{ ...cellControl, width: 220 }}>
                          <option value="">Статья</option>
                          {['income', 'expense', 'below', 'result'].map((group) => {
                            const options = entryArticles.filter((item) => item.group === group);
                            if (!options.length) return null;
                            const title = { income: 'Доходы', expense: 'Расходы', below: 'Ниже прибыли', result: 'Результат' }[group];
                            return (
                              <optgroup key={group} label={title}>
                                {options.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
                              </optgroup>
                            );
                          })}
                        </select>
                      ) : row.article_name}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{fmt(row.amount)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{{ dds: 'ДДС', pnl: 'P&L', both: 'Оба' }[row.statement] || (row.unmatched ? '—' : '')}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text2)' }}>{row.note}</td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
                      {row.unmatched ? (
                        <button type="button" onClick={() => assignBank(row).catch((err) => setError(err?.response?.data?.detail || 'Не разнеслось'))} style={rowButtonStyle}>Разнести</button>
                      ) : (
                        <button type="button" onClick={() => {
                          if (!window.confirm('Удалить строку разноски?')) return;
                          api.delete(`/finance/entries/${row.id}`).then(loadEntries);
                        }} style={rowButtonStyle}>Удалить</button>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {!entries.length && (
                  <tr><td colSpan={7} style={{ padding: 28, textAlign: 'center', color: 'var(--text2)' }}>За этот месяц разноски ещё нет</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'archive' && (
        <div className="fin-archive">
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 12, maxHeight: 'calc(100vh - 240px)', overflow: 'auto' }}>
            <input value={sheetQuery} placeholder="Найти вкладку" onChange={(event) => setSheetQuery(event.target.value)} style={{ ...controlStyle, marginBottom: 8 }} />
            {sheets.map((item) => (
              <button key={item.name} type="button" title={item.name} onClick={() => { setSheet(item.name); setArchiveOffset(0); }} style={{
                display: 'block', width: '100%', height: 36, textAlign: 'left', padding: '0 10px',
                marginBottom: 4, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                border: `1px solid ${sheet === item.name ? 'var(--accent)' : 'var(--border)'}`,
                background: sheet === item.name ? 'var(--accent)' : '#fff',
                color: sheet === item.name ? '#fff' : 'var(--text1)',
              }}>{item.name.trim()}</button>
            ))}
          </div>
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            {!sheet && <div style={{ padding: 40, color: 'var(--text2)' }}>Выберите вкладку слева. Здесь лежит вся книга, включая старые формы.</div>}
            {sheet && archive && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 800, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{archive.name.trim()}</div>
                  <button type="button" disabled={archiveOffset <= 0} onClick={() => setArchiveOffset(Math.max(0, archiveOffset - 120))} style={{
                    height: 36, minWidth: 120, borderRadius: 8, border: '1px solid var(--border)', background: '#fff',
                    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>Назад</button>
                  <button type="button" disabled={archiveOffset + 120 >= archive.total} onClick={() => setArchiveOffset(archiveOffset + 120)} style={{
                    height: 36, minWidth: 120, borderRadius: 8, border: '1px solid var(--border)', background: '#fff',
                    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>Дальше</button>
                </div>
                <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                      {archive.rows.map((row, index) => (
                        <tr key={archive.offset + index}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} style={{
                              padding: '4px 8px', borderBottom: '1px solid var(--border)',
                              borderRight: '1px solid var(--border)', whiteSpace: 'nowrap',
                              position: cellIndex === 0 ? 'sticky' : undefined, left: 0,
                              background: '#fff', fontWeight: cellIndex === 0 ? 700 : 500,
                            }}>{cell === '' || cell === null ? '' : String(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {planProject && (
        <PlanModal
          row={{ name: `План поступлений · ${planProject.full || planProject.name}`, plan: planProject.plan || '' }}
          onClose={() => setPlanProject(null)}
          onSave={async (value) => {
            await api.post('/finance/plan', {
              period, statement: 'both', unit_code: planProject.code, article_code: 'income_main', value,
            });
            setPlanProject(null);
            const response = await api.get('/finance/statement', { params: { period, statement: tab, unit } });
            setReport(response.data);
          }}
        />
      )}

      {planRow && (
        <PlanModal
          row={planRow}
          onClose={() => setPlanRow(null)}
          onSave={async (value) => {
            await api.post('/finance/plan', {
              period, statement: tab, unit_code: unit, article_code: planRow.code, value,
            });
            setPlanRow(null);
            const response = await api.get('/finance/statement', { params: { period, statement: tab, unit } });
            setReport(response.data);
          }}
        />
      )}
    </div>
  );
}
